import { trackYouLeadProduct } from "./analytics/track-event"

class FrontPresentationSelector {
  constructor(containerId, optionsMap = {}) {
    this.container = document.getElementById(containerId)
    this.optionsMap = optionsMap

    if (!this.container) {
      console.error(`Container #${containerId} not found`)
      return
    }

    this.detectSelectorTypes()
    this.init()
  }

  detectSelectorTypes() {
    this.finishSection = this.container.querySelector("[data-type='finish']")
    this.colorSection = this.container.querySelector("[data-type='color']")
    this.modelSection = this.container.querySelector("[data-type='model']")
    this.handleSection = this.container.querySelector("[data-type='handle']")
    this.variantSection = this.container.querySelector("[data-type='variant']")
  }

  /**
   * Initialize the component
   */
  init() {
    this.initShowMoreButtons()

    if (this.colorSection || this.finishSection) {
      this.initFinishButtons()
      this.initColorButtons()
    }

    if (this.modelSection) {
      this.initModelButtons()
    }

    if (this.handleSection) {
      this.initHandleButtons()
    }

    if (this.variantSection) {
      this.initVariantButtons()
    }
  }

  initShowMoreButtons() {
    const moreButtons = this.container.querySelectorAll(
      ".element-details__configurations__section__options-wrapper__more-options",
    )

    moreButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const model = button.getAttribute("data-model")
        const rowsContainer = this.container.querySelector(
          `.element-details__configurations__section__options-wrapper__rows[data-model="${model}"]`,
        )

        if (!rowsContainer) {
          return
        }

        const moreText = button.querySelector(
          ".element-details__configurations__section__options-wrapper__more-options--more",
        )
        const lessText = button.querySelector(
          ".element-details__configurations__section__options-wrapper__more-options--less",
        )

        const isExpanding = moreText.style.display !== "none"
        moreText.style.display = isExpanding ? "none" : "inline"
        lessText.style.display = isExpanding ? "inline" : "none"

        const allRows = rowsContainer.querySelectorAll(
          ".element-details__configurations__section__options-wrapper__rows__row",
        )
        const rowsToToggle = Array.from(allRows).slice(1)

        rowsToToggle.forEach((row) => {
          row.classList.add(
            "element-details__configurations__section__options-wrapper__rows__row--next",
          )
        })

        rowsToToggle.forEach((row) => {
          if (isExpanding) {
            row.classList.add("visible")
            row.style.display = ""
          } else {
            row.classList.remove("visible")
            row.style.display = "none"
          }
        })

        rowsContainer.classList.toggle(
          "element-details__configurations__section__options-wrapper__rows--hidden-rows",
        )
      })
    })
  }

  sendYlEvent(ylId) {
    if (!ylId) {
      return
    }

    trackYouLeadProduct([ylId])
  }

  initButtons(section, fn) {
    if (!section) {
      return
    }

    const buttons = section.querySelectorAll("[data-option-codename]")

    buttons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.stopPropagation()

        const wasActive = button.classList.contains("active")
        const isActive = !wasActive

        buttons.forEach((btn) => btn.classList.remove("active"))

        if (isActive) {
          button.classList.add("active")
        }

        this.sendYlEvent(button.getAttribute("data-yl-id"))

        const activeCodename = isActive
          ? button.getAttribute("data-option-codename")
          : null

        fn(activeCodename)
      })
    })
  }

  initFinishButtons() {
    this.initButtons(this.finishSection, (codename) => {
      this.updateOptions(this.colorSection, "color", codename)
      this.selectedFinishType = codename
      this.selectedColor = null
      this.updateNotice()
    })
  }

  initColorButtons() {
    this.initButtons(this.colorSection, (codename) => {
      this.updateOptions(this.modelSection, "model", codename)
      this.updateLinks("color", codename)
      this.selectedColor = codename
      this.updateNotice()
    })
  }

  updateNotice() {
    const noticeContainer = this.container.querySelector(".notice-container")

    if (!noticeContainer) {
      return
    }

    const defaultNotice = noticeContainer.querySelector(".notice-default")
    const lacqueredNotice = noticeContainer.querySelector(".notice-lacquered")

    if (!defaultNotice || !lacqueredNotice) {
      return
    }

    const lacqueredCodenames = window.lacqueredColorCodenames || []
    const isLacquered =
      this.selectedFinishType === "color" ||
      (this.selectedColor &&
        lacqueredCodenames.includes(this.selectedColor.toLowerCase()))

    defaultNotice.style.display = isLacquered ? "none" : ""
    lacqueredNotice.style.display = isLacquered ? "" : "none"
  }

  initModelButtons() {
    this.initButtons(this.modelSection, (codename) => {
      this.updateLinks("model", codename)
    })
  }

  initHandleButtons() {
    this.initButtons(this.handleSection, (codename) => {
      this.updateLinks("handle", codename)
    })
  }

  initVariantButtons() {
    this.initButtons(this.variantSection, (codename) => {
      this.updateLinks("texture", codename)
    })
  }

  initButtonsByType(type) {
    switch (type) {
      case "finish":
        return this.initFinishButtons()
      case "color":
        return this.initColorButtons()
      case "model":
        return this.initModelButtons()
      case "handle":
        return this.initHandleButtons()
      case "variant":
        return this.initVariantButtons()
    }
  }

  updateOptions(section, type, categoryCodename) {
    if (!section) {
      return
    }

    const availableCodenames = this.getAvailableCodenames(
      type,
      categoryCodename,
    )

    const rowsContainer = section.querySelector(
      ".element-details__configurations__section__options-wrapper__rows",
    )

    if (!rowsContainer._originalOptions) {
      rowsContainer._originalOptions = Array.from(
        section.querySelectorAll("[data-option-codename]"),
      )
    }

    const existingRows = rowsContainer.querySelectorAll(
      ".element-details__configurations__section__options-wrapper__rows__row",
    )
    existingRows.forEach((row) => row.remove())

    const allOptions = rowsContainer._originalOptions
    const visibleOptions =
      availableCodenames.length > 0
        ? allOptions.filter((option) => {
            const codename = option.getAttribute("data-option-codename")

            return availableCodenames.includes(codename)
          })
        : [...allOptions]

    for (let i = 0; i < visibleOptions.length; i += 5) {
      const rowElement = document.createElement("div")
      rowElement.className =
        "element-details__configurations__section__options-wrapper__rows__row"
      rowElement.setAttribute(
        "data-model",
        rowsContainer.getAttribute("data-model"),
      )

      if (i > 0) {
        rowElement.classList.add(
          "element-details__configurations__section__options-wrapper__rows__row--next",
        )
      }

      visibleOptions.slice(i, i + 5).forEach((option) => {
        rowElement.appendChild(option.cloneNode(true))
      })

      rowsContainer.appendChild(rowElement)
    }

    this.initButtonsByType(type)

    const moreOptionsWrapper = section.querySelector(
      ".element-details__configurations__section__options-wrapper__more-options",
    )

    if (moreOptionsWrapper) {
      const hasMultipleRows = visibleOptions.length > 5
      moreOptionsWrapper.style.display = hasMultipleRows ? "flex" : "none"

      moreOptionsWrapper.children[0].style.display = "block"
      moreOptionsWrapper.children[1].style.display = "none"
    }

    this.updateLinks(type)
  }

  updateLinks(type, optionCodename = null) {
    const links = this.container.querySelectorAll(".configure")

    links.forEach((link) => {
      const url = new URL(link.href, window.location.origin)

      if (optionCodename) {
        url.searchParams.set(type, optionCodename)
      } else {
        url.searchParams.delete(type)
      }

      link.href = url.toString()
    })
  }

  getAvailableCodenames(type, categoryCodename) {
    const optionsMap = this.optionsMap[type]

    if (!optionsMap) {
      return []
    }

    const options = optionsMap[categoryCodename]

    if (!options) {
      return []
    }

    return options.map((item) => item.codename)
  }

  static initializeContainers(containerIds, optionsMap = {}) {
    if (!containerIds || containerIds.length === 0) {
      return []
    }

    return containerIds.map(
      (id) => new FrontPresentationSelector(id, optionsMap),
    )
  }
}

export default FrontPresentationSelector
window.FrontPresentationSelector = FrontPresentationSelector
